import React, {
  useEffect,
  useState,
} from 'react';
import getConfig from 'next/config';
import styled from 'styled-components';

import { buildPixelSrc } from '@/utils/beacon';

const { REACT_APP_LANDING_VERSION } = getConfig().publicRuntimeConfig;

const Pixel = styled.img`
  position: absolute;
  visibility: hidden;
  width: 1px;
  height: 1px;
`;

interface DetailsLike {
  organization?: { uuid?: string };
  version?: string;
}

export interface MetricsPixelProps {
  pageProps: {
    actionCenterDetails?: DetailsLike;
    signupDetails?: DetailsLike;
    statusCode?: number;
  };
}

/**
 * Access-metrics pixel, mounted once in pages/_app.tsx. Loading the gif IS
 * the page_viewed event (see app/utils/beacon.ts); the beacon endpoint
 * derives the page type from the URL and silently drops untracked routes.
 *
 * The <img> is only rendered client-side, after mount: page HTML is
 * edge-cached, so the per-pageload session id in the src must never appear
 * in server-rendered markup.
 *
 * Not rendered on 404/error responses (statusCode from _app), so missing
 * records and unmatched routes never register a page view.
 */
export const MetricsPixel = ({ pageProps }: MetricsPixelProps) => {
  const [
    src,
    setSrc,
  ] = useState<string | null>(null);

  useEffect(() => {
    if (pageProps.statusCode && pageProps.statusCode >= 400) {
      return;
    }

    const details = pageProps.actionCenterDetails || pageProps.signupDetails;

    setSrc(buildPixelSrc({
      organization: details?.organization?.uuid,
      version: REACT_APP_LANDING_VERSION || details?.version,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!src) {
    return null;
  }

  return (
    <Pixel
      alt=""
      aria-hidden="true"
      height={1}
      src={src}
      width={1}
    />
  );
};
